import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, Chip, Divider,
  CircularProgress, Alert, ToggleButton, ToggleButtonGroup,
  TextField, Paper, Stack, Tooltip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddAlarmIcon from "@mui/icons-material/AddAlarm";
import {
  getHorariosEnvioScraper,
  crearHorariosEnvioScraper,
  eliminarHorarioEnvioScraper,
} from "../../componentes/http/actions/HorarioEnvioScraperAction";

const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const DIAS_COMPLETOS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

const HorarioEnvioScraperPanel = () => {
  const [horarios, setHorarios]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [guardando, setGuardando]   = useState(false);
  const [error, setError]           = useState(null);
  const [exito, setExito]           = useState(null);

  // Formulario nuevo horario
  const [diasSeleccionados, setDiasSeleccionados] = useState([]);
  const [horaSeleccionada, setHoraSeleccionada]   = useState("09:00");

  const token = localStorage.getItem("token");

  const cargarHorarios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getHorariosEnvioScraper(token);
      setHorarios(data);
    } catch (err) {
      setError("Error al cargar los horarios.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    cargarHorarios();
  }, [cargarHorarios]);

  const handleGuardar = async () => {
    if (diasSeleccionados.length === 0) {
      setError("Seleccioná al menos un día.");
      return;
    }
    if (!horaSeleccionada) {
      setError("Seleccioná una hora.");
      return;
    }

    setGuardando(true);
    setError(null);
    setExito(null);

    try {
      // Convertir "HH:mm" a TimeSpan string "HH:mm:00"
      const horaEnvio = `${horaSeleccionada}:00`;

      await crearHorariosEnvioScraper(token, {
        dias: diasSeleccionados,
        horaEnvio,
      });

      setExito(`${diasSeleccionados.length} horario(s) creado(s) correctamente.`);
      setDiasSeleccionados([]);
      setHoraSeleccionada("09:00");
      await cargarHorarios();
    } catch (err) {
      const msg = err?.response?.data?.error || "Error al guardar el horario.";
      setError(msg);
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id) => {
    try {
      await eliminarHorarioEnvioScraper(token, id);
      setHorarios((prev) => prev.filter((h) => h.id !== id));
      setExito("Horario eliminado.");
    } catch {
      setError("Error al eliminar el horario.");
    }
  };

  // Agrupar horarios por hora para mostrarlos ordenados
  const horariosAgrupados = horarios.reduce((acc, h) => {
    const key = h.horaDisplay;
    if (!acc[key]) acc[key] = [];
    acc[key].push(h);
    return acc;
  }, {});

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} mb={2}>
        Horarios de envío del scraper
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {exito && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setExito(null)}>
          {exito}
        </Alert>
      )}

      {/* Formulario nuevo horario */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
          Agregar nuevo horario
        </Typography>

        <Stack spacing={2}>
          {/* Selector de días */}
          <Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Días de envío
            </Typography>
            <ToggleButtonGroup
              value={diasSeleccionados}
              onChange={(_, newDias) => setDiasSeleccionados(newDias)}
              size="small"
              sx={{ flexWrap: "wrap", gap: 0.5 }}
            >
              {DIAS.map((dia, idx) => (
                <ToggleButton
                  key={idx}
                  value={idx}
                  sx={{
                    px: 1.5,
                    fontWeight: 600,
                    "&.Mui-selected": {
                      bgcolor: "primary.main",
                      color: "white",
                      "&:hover": { bgcolor: "primary.dark" },
                    },
                  }}
                >
                  {dia}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Selector de hora */}
          <Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Hora de envío (hora local)
            </Typography>
            <TextField
              type="time"
              size="small"
              value={horaSeleccionada}
              onChange={(e) => setHoraSeleccionada(e.target.value)}
              inputProps={{ step: 300 }} // Intervalos de 5 min
              sx={{ width: 160 }}
            />
          </Box>

          <Box>
            <Button
              variant="contained"
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <AddAlarmIcon />}
              onClick={handleGuardar}
              disabled={guardando}
              size="small"
            >
              {guardando ? "Guardando..." : "Agregar horario"}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Divider sx={{ mb: 2 }} />

      {/* Lista de horarios actuales */}
      <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
        Horarios configurados
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={28} />
        </Box>
      ) : horarios.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No hay horarios configurados.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {Object.entries(horariosAgrupados)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([hora, items]) => (
              <Paper key={hora} variant="outlined" sx={{ p: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="body2" fontWeight={700} sx={{ minWidth: 55 }}>
                    {hora}
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5} flex={1}>
                    {items
                      .sort((a, b) => a.diaSemana - b.diaSemana)
                      .map((h) => (
                        <Chip
                          key={h.id}
                          label={DIAS_COMPLETOS[h.diaSemana]}
                          size="small"
                          color="primary"
                          variant="outlined"
                          onDelete={() => handleEliminar(h.id)}
                          deleteIcon={
                            <Tooltip title="Eliminar">
                              <DeleteIcon fontSize="small" />
                            </Tooltip>
                          }
                        />
                      ))}
                  </Box>
                </Box>
              </Paper>
            ))}
        </Stack>
      )}
    </Box>
  );
};

export default HorarioEnvioScraperPanel;